import React, { useCallback, useEffect, useRef } from 'react';
import styles from './signature-field.module.css';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Tabs } from '@dropbox/dig-components/tabs';
import { UIIcon } from '@dropbox/dig-icons';
import {
  CameraUploadLine,
  CloudDoneLine,
  KeyboardLine,
  SignatureLine,
} from '@dropbox/dig-icons/assets';
import { Box } from '@dropbox/dig-foundations';
import { Text } from '@dropbox/dig-components/typography';
import constants from '../constants';

import Draw from '../draw';
import Upload from '../upload/upload';
import Saved from '../saved/saved';
import { TypeSignature } from '../type/type';
import { ErrorBanner } from '../../utils/error-handling/error-banner';
import { TabIconContainer } from '../common/tab-icon-container';
import { SignatureModalContext } from '../signature-modal-context/context';
import { SignatureType } from '../types';
import { SignatureModalBody } from '../signature-modal-v2';
import { ValueOfArray } from '../../utils/value-of-array';

const messages = defineMessages({
  drawTabAriaLabel: {
    id: 'fc33afe09973f2a11ad39edda2a078a3426159f6eed00b74873b7b9eedc67ff4',
    description:
      'Screen reader label for the tab where user can draw their signature.',
    defaultMessage: 'Draw your signature',
  },
  typeTabAriaLabel: {
    id: '54a51e0edcdde9d841e3e7d93937d22e49c485d05fc6a64e55be5382c56bee59',
    description:
      'Screen reader label for the tab where user can type in their signature.',
    defaultMessage: 'Type in your signature',
  },
  uploadTabAriaLabel: {
    id: '8899c7033574762a92d3f489a0b7fcd49e34255f9df80c8594e8e3785033c1fe',
    description:
      'Screen reader label for the tab where user can upload a photo of their signature.',
    defaultMessage: 'Upload a photo of your signature',
  },
  savedTabAriaLabel: {
    id: '449fee316cc9352c99b8db6213096738be86cd716a234a6fb72f95cf703984d0',
    description:
      'Screen reader label for the tab where user can pick a saved signature.',
    defaultMessage: 'Choose one of your saved signatures',
  },
});

export type SignatureFieldProps = {
  signatureModalBodyRef: React.RefObject<HTMLElement & SignatureModalBody>;
  loading: boolean;
  type: SignatureType;
  initialTypeInValue: string;
  enableInsertButtonCallback: (enableInsert: boolean) => void;
  onUploadSignature: SignatureModalContext['onUploadSignature'];
  selectedTab: SignatureModalContext['selectedTab'];
  savedSignatures: SignatureModalContext['savedSignatures'];
  getSignatureUrl: SignatureModalContext['getSignatureUrl'];
  onSavedSignatureSelect: SignatureModalContext['onSavedSignatureSelect'];
  onSavedSignatureRemove: SignatureModalContext['onSavedSignatureRemove'];
  selectedSavedSignature: SignatureModalContext['selectedSavedSignature'];
  isUploadingSignature: SignatureModalContext['isUploadingSignature'];
  uploadedSignature: SignatureModalContext['uploadedSignature'];
  onRotateSignature: SignatureModalContext['onRotateSignature'];
  clearUploadedSignature: SignatureModalContext['clearUploadedSignature'];
  getPaginatedSignatures?: SignatureModalContext['getPaginatedSignatures'];
  initialsCount?: SignatureModalContext['initialsCount'];
  signaturesCount?: SignatureModalContext['signaturesCount'];
  error?: SignatureModalContext['error'];
  clearSignatureError: SignatureModalContext['clearSignatureError'];
  enabledTabs: SignatureModalContext['enabledTabs'];
  onTabSelect: SignatureModalContext['onTabSelect'];
};

export type Tab = ValueOfArray<typeof constants.TABS>;
function isTab(tab: string): tab is Tab {
  return constants.TABS.includes(tab as Tab);
}

const TAB_ID_PREFIX = 'signature-modal-tab-';
const tabId = (tab: Tab) => `${TAB_ID_PREFIX}${tab}`;

function SignatureField({
  signatureModalBodyRef,
  type,
  initialTypeInValue,
  loading,
  selectedTab,
  savedSignatures,
  getSignatureUrl,
  onSavedSignatureSelect,
  onSavedSignatureRemove,
  selectedSavedSignature,
  isUploadingSignature,
  uploadedSignature,
  onRotateSignature,
  clearUploadedSignature,
  getPaginatedSignatures,
  initialsCount,
  signaturesCount,
  error,
  clearSignatureError,
  enableInsertButtonCallback,
  onUploadSignature,
  enabledTabs,
  onTabSelect,
}: SignatureFieldProps) {
  const intl = useIntl();

  const shouldShowTab = useCallback(
    (tab: Tab): boolean => {
      if (enabledTabs) {
        return enabledTabs.includes(tab);
      }
      return true;
    },
    [enabledTabs],
  );

  const handleTabSelection = (tabId: string) => {
    const tab = tabId.replace(TAB_ID_PREFIX, '');
    if (isTab(tab)) {
      onTabSelect(tab);
    }
  };

  const shouldUpdateTab = useRef(
    !shouldShowTab(selectedTab) ||
      (selectedTab === constants.TAB_SAVED && savedSignatures.length === 0),
  );

  const firstEnabledTab = useRef(
    enabledTabs?.find((type) => type !== selectedTab),
  );

  const updateSelectedTabIfCurrentUnavailable = useCallback(() => {
    if (shouldUpdateTab.current) {
      if (shouldShowTab(constants.TAB_DRAW)) {
        onTabSelect(constants.TAB_DRAW);
      } else if (firstEnabledTab.current) {
        onTabSelect(firstEnabledTab.current);
      }
    }
  }, [onTabSelect, shouldShowTab]);

  useEffect(() => {
    updateSelectedTabIfCurrentUnavailable();
  }, [updateSelectedTabIfCurrentUnavailable]);

  const uploadSignature = (file: File) => {
    onUploadSignature(file, type);
  };

  const count =
    type === constants.TYPE_SIGNATURE ? signaturesCount : initialsCount;

  return (
    <div>
      {loading && selectedTab !== constants.TAB_UPLOAD ? (
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'center'}
          alignItems={'center'}
          borderColor={'Border Subtle - On Base'}
          borderWidth={'1'}
          borderStyle={'Solid'}
          className={styles.loading}
        >
          <Text>
            <FormattedMessage
              id="62da6497bb4cf0cea5b8ab173e34a5bbda7cc7b63bbb6f0334a5afa29bfa584c"
              description="Loading message"
              defaultMessage="Loading…"
            />
          </Text>
        </Box>
      ) : (
        <Tabs selectedTab={tabId(selectedTab)} onSelection={handleTabSelection}>
          <Tabs.Group containerClassName={styles.tabs}>
            {shouldShowTab(constants.TAB_DRAW) && (
              <Tabs.Tab
                id={tabId(constants.TAB_DRAW)}
                aria-label={intl.formatMessage(messages.drawTabAriaLabel)}
                data-testid={`signing-modal--${constants.TAB_DRAW}`}
                data-qa-ref={`signing-modal--${constants.TAB_DRAW}`}
              >
                <TabIconContainer icon={<UIIcon src={SignatureLine} />}>
                  <FormattedMessage
                    id="4a93b7e6eeb4849fa9cc4f247db5068669ca1d7cbeededfaf078a17b765ef4c4"
                    description="Label text for the tab where user can draw in their signature."
                    defaultMessage="Draw"
                  />
                </TabIconContainer>
              </Tabs.Tab>
            )}
            {shouldShowTab(constants.TAB_TYPE) && (
              <Tabs.Tab
                id={tabId(constants.TAB_TYPE)}
                aria-label={intl.formatMessage(messages.typeTabAriaLabel)}
                data-testid={`signing-modal--${constants.TAB_TYPE}`}
                data-qa-ref={`signing-modal--${constants.TAB_TYPE}`}
              >
                <TabIconContainer icon={<UIIcon src={KeyboardLine} />}>
                  <FormattedMessage
                    id="f92cf2c311804d5f1599a446aba5788f6af02db3fd215435ce44ec4de18532dd"
                    description="Label text for the tab where user can type in their signature."
                    defaultMessage="Type"
                  />
                </TabIconContainer>
              </Tabs.Tab>
            )}
            {shouldShowTab(constants.TAB_UPLOAD) && (
              <Tabs.Tab
                id={tabId(constants.TAB_UPLOAD)}
                aria-label={intl.formatMessage(messages.uploadTabAriaLabel)}
                data-testid={`signing-modal--${constants.TAB_UPLOAD}`}
                data-qa-ref={`signing-modal--${constants.TAB_UPLOAD}`}
              >
                <TabIconContainer icon={<UIIcon src={CameraUploadLine} />}>
                  <FormattedMessage
                    id="56d3d6c1b908add6895a510b5f479c9408b11fd5752f826ac7ad7b42444b39b0"
                    description="Label text for an icon that allows the user to upload an image of their signature."
                    defaultMessage="Upload"
                  />
                </TabIconContainer>
              </Tabs.Tab>
            )}
            {shouldShowTab(constants.TAB_SAVED) && (
              <Tabs.Tab
                id={tabId(constants.TAB_SAVED)}
                aria-label={intl.formatMessage(messages.savedTabAriaLabel)}
                data-testid={`signing-modal--${constants.TAB_SAVED}`}
                data-qa-ref={`signing-modal--${constants.TAB_SAVED}`}
              >
                <TabIconContainer icon={<UIIcon src={CloudDoneLine} />}>
                  <FormattedMessage
                    id="4f3be2e4fd81ddb5fbd21e7301be5c5da31603c814463a40079392c9310251f7"
                    description="Label text for an icon that allows the user to view their saved signature."
                    defaultMessage="Saved"
                  />
                </TabIconContainer>
              </Tabs.Tab>
            )}
          </Tabs.Group>
          {error && (
            <Box marginTop={'Micro Small'}>
              <ErrorBanner error={error} onClose={clearSignatureError} />{' '}
            </Box>
          )}
          <Box
            marginTop={'Micro Large'}
            position={'relative'}
            className={styles.panelContentWrapper}
          >
            {shouldShowTab(constants.TAB_DRAW) && (
              <Tabs.Panel
                tabId={tabId(constants.TAB_DRAW)}
                className={styles.panelContent}
              >
                <Draw
                  ref={signatureModalBodyRef}
                  enableInsertButtonCallback={enableInsertButtonCallback}
                  defaultCanvasHeight={180}
                  defaultCanvasWidth={652}
                  isNewSignerApp
                />
              </Tabs.Panel>
            )}
            {shouldShowTab(constants.TAB_TYPE) && (
              <Tabs.Panel
                tabId={tabId(constants.TAB_TYPE)}
                className={styles.panelContent}
              >
                <TypeSignature
                  ref={signatureModalBodyRef}
                  enableInsertButtonCallback={enableInsertButtonCallback}
                  initialTypeInValue={initialTypeInValue}
                  isNewSignerApp
                />
              </Tabs.Panel>
            )}
            {shouldShowTab(constants.TAB_UPLOAD) && (
              <Tabs.Panel
                tabId={tabId(constants.TAB_UPLOAD)}
                className={styles.panelContent}
              >
                <Upload
                  ref={signatureModalBodyRef}
                  isUploadingSignature={isUploadingSignature}
                  uploadedSignature={uploadedSignature}
                  onRotateSignature={onRotateSignature}
                  uploadSignature={uploadSignature}
                  getSignatureUrl={getSignatureUrl}
                  enableInsertButtonCallback={enableInsertButtonCallback}
                  clearUploadedSignature={clearUploadedSignature}
                  isMobile={false}
                  isNewSignerApp
                />
              </Tabs.Panel>
            )}
            {shouldShowTab(constants.TAB_SAVED) && (
              <Tabs.Panel
                tabId={tabId(constants.TAB_SAVED)}
                className={styles.panelContent}
              >
                <Saved
                  ref={signatureModalBodyRef}
                  type={type}
                  enableInsertButtonCallback={enableInsertButtonCallback}
                  signatures={savedSignatures}
                  getSignatureUrl={getSignatureUrl}
                  selectedSavedSignature={selectedSavedSignature}
                  onSavedSignatureRemove={onSavedSignatureRemove}
                  onSavedSignatureSelect={onSavedSignatureSelect}
                  getPaginatedSignatures={getPaginatedSignatures}
                  count={count ?? 0}
                  isMobile={false}
                  isNewSignerApp
                />
              </Tabs.Panel>
            )}
          </Box>
        </Tabs>
      )}
    </div>
  );
}

export default SignatureField;
